import { useMutation, useQuery, useQueryClient } from 'react-query'
import { apiClient } from '@/lib/api'
import type {
  AnalyticPartDefinition,
  BankAccount,
  Match,
  MiragePosting,
  OpenItem,
  CommitResult,
  MatchKind,
  CounterpartKind,
  Transaction,
} from '../types'

const RECON_BASE_URL =
  import.meta.env.VITE_GO_API_URL || 'http://localhost:8081'

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('access_token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

async function reconFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const res = await fetch(`${RECON_BASE_URL}${path}`, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options?.headers,
    },
  })

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText)
    throw new Error(`Recon API ${res.status}: ${text}`)
  }

  return res.json()
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function useBankAccounts(clientId: string | null) {
  return useQuery<BankAccount[]>(
    ['recon', 'bank-accounts', clientId],
    () =>
      reconFetch<BankAccount[]>(
        `/api/v1/reconciliation/clients/${clientId}/bank-accounts`
      ),
    { enabled: !!clientId }
  )
}

export function useAccountAnalytics(
  clientId: string | null,
  accountId: number | null
) {
  return useQuery<AnalyticPartDefinition>(
    ['recon', 'account-analytics', clientId, accountId],
    () =>
      reconFetch<AnalyticPartDefinition>(
        `/api/v1/reconciliation/clients/${clientId}/accounts/${accountId}/analytics`
      ),
    {
      enabled: !!clientId && accountId != null && accountId > 0,
      staleTime: 5 * 60_000,
    }
  )
}

export interface UploadedFile {
  file_id: string
  filename: string
  mime_type: string
  size_bytes: number
  uploaded_at: string
  transactions_extracted: number
  processing_complete: boolean
}

export function useUploadedFiles() {
  return useQuery<UploadedFile[]>(
    ['recon', 'files'],
    async () => {
      const res = await apiClient.get<{ files: UploadedFile[] }>(
        '/reconciliation/files'
      )
      return res.files
    }
  )
}

export interface DetailedFile {
  id: string
  original_filename: string
  mime_type: string
  size_bytes: number | null
  source: string
  ingested_at: string
  client_id: string | null
  client_name: string | null
  transaction_count: number
  status: string
  has_committed_links: boolean
  committed_link_count: number
  bank_account_id: number | null
}

export function useClientFiles(clientId: string | null) {
  return useQuery<DetailedFile[]>(
    ['recon', 'files-detailed', clientId],
    async () => {
      const res = await apiClient.get<{ files: DetailedFile[] }>(
        `/reconciliation/files/detailed?client_id=${clientId}`
      )
      return res.files
    },
    {
      enabled: !!clientId,
      refetchInterval: (data) => {
        if (data?.some((f) => f.status === 'processing')) return 3000
        return false
      },
    }
  )
}

export interface FileStatus {
  file_id: string
  filename: string
  status: string
  message: string
  transactions_count: number
  uploaded_at: string
}

export function useFileStatus(fileId: string | null) {
  return useQuery<FileStatus>(
    ['recon', 'file-status', fileId],
    () =>
      apiClient.get<FileStatus>(`/reconciliation/upload/${fileId}/status`),
    { enabled: !!fileId, refetchInterval: 3000 }
  )
}

export interface DeleteCheckResult {
  can_delete: boolean
  has_committed_matches: boolean
  committed_match_count: number
  transaction_count: number
  warnings: string[]
}

export function useFileDeleteCheck(fileId: string | null) {
  return useQuery<DeleteCheckResult>(
    ['recon', 'delete-check', fileId],
    () =>
      reconFetch<DeleteCheckResult>(
        `/api/v1/reconciliation/files/${fileId}/delete-check`
      ),
    { enabled: !!fileId }
  )
}

export function useDeleteFile() {
  const qc = useQueryClient()
  return useMutation(
    ({ fileId, force = false }: { fileId: string; force?: boolean }) =>
      reconFetch<{
        deleted: boolean
        file_id: string
        transactions_deleted: number
        matches_deleted: number
        warnings: string[]
      }>(`/api/v1/reconciliation/files/${fileId}?force=${force}`, {
        method: 'DELETE',
      }),
    {
      onSuccess: () => {
        qc.invalidateQueries(['recon', 'files-detailed'])
        qc.invalidateQueries(['recon', 'transactions'])
        qc.invalidateQueries(['recon', 'matches'])
      },
    }
  )
}

export function useAccounts(clientId: string | null) {
  return useQuery<BankAccount[]>(
    ['recon', 'accounts', clientId],
    () =>
      reconFetch<BankAccount[]>(
        `/api/v1/reconciliation/clients/${clientId}/accounts`
      ),
    { enabled: !!clientId }
  )
}

export function useAllOpenItems(clientId: string | null) {
  return useQuery<OpenItem[]>(
    ['recon', 'open-items-all', clientId],
    () =>
      reconFetch<OpenItem[]>(
        `/api/v1/reconciliation/clients/${clientId}/open-items`,
        {
          method: 'POST',
          body: JSON.stringify({}),
        }
      ),
    { enabled: !!clientId }
  )
}

export function useOpenItems(clientId: string | null, accountIds: number[]) {
  return useQuery<OpenItem[]>(
    ['recon', 'open-items', clientId, accountIds],
    () =>
      reconFetch<OpenItem[]>(
        `/api/v1/reconciliation/clients/${clientId}/open-items`,
        {
          method: 'POST',
          body: JSON.stringify({ account_ids: accountIds }),
        }
      ),
    { enabled: !!clientId && accountIds.length > 0 }
  )
}

export function useMatches(
  clientId: string | null,
  statementId?: string | null,
  state?: string | null
) {
  const params = new URLSearchParams()
  if (statementId) params.set('statement_id', statementId)
  if (state) params.set('state', state)
  const qs = params.toString()

  return useQuery<Match[]>(
    ['recon', 'matches', clientId, statementId, state],
    () =>
      reconFetch<Match[]>(
        `/api/v1/reconciliation/clients/${clientId}/matches${qs ? `?${qs}` : ''}`
      ),
    { enabled: !!clientId }
  )
}

interface TransactionsResponse {
  transactions: Array<{
    id: string
    description: string
    reference: string | null
    amount: number
    booking_ts: string | null
    counterparty_name: string | null
    currency: string
    client_id: string | null
    links: unknown[]
  }>
  total: number
}

export function useTransactions(clientId: string | null, fileId?: string | null) {
  return useQuery<Transaction[]>(
    ['recon', 'transactions', clientId, fileId],
    async () => {
      const params = new URLSearchParams({ client_ids: clientId!, limit: '1000' })
      if (fileId) params.set('file_id', fileId)
      const res = await apiClient.get<TransactionsResponse>(
        `/reconciliation/transactions?${params}`
      )
      const txns = res.transactions.map((t) => ({
        id: t.id,
        statement_id: '',
        date: t.booking_ts?.split('T')[0] ?? '',
        amount: t.amount,
        currency: t.currency || 'EUR',
        counterparty: t.counterparty_name || '—',
        description: t.description || '',
        reference: t.reference ?? undefined,
      }))
      txns.sort((a, b) => a.date.localeCompare(b.date))
      return txns
    },
    { enabled: !!clientId }
  )
}

export function useMatch(matchId: string | null) {
  return useQuery<Match>(
    ['recon', 'match', matchId],
    () => reconFetch<Match>(`/api/v1/reconciliation/matches/${matchId}`),
    { enabled: !!matchId }
  )
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useConfirmMatch() {
  const qc = useQueryClient()
  return useMutation(
    (matchId: string) =>
      reconFetch<Match>(`/api/v1/reconciliation/matches/${matchId}/confirm`, {
        method: 'POST',
      }),
    { onSuccess: () => qc.invalidateQueries(['recon', 'matches']) }
  )
}

export function useRejectMatch() {
  const qc = useQueryClient()
  return useMutation(
    (matchId: string) =>
      reconFetch<Match>(`/api/v1/reconciliation/matches/${matchId}/reject`, {
        method: 'POST',
      }),
    { onSuccess: () => qc.invalidateQueries(['recon', 'matches']) }
  )
}

interface CreateMatchPayload {
  transaction_id: string
  client_id: string
  mirage_bank_account_id: number
  match_kind: MatchKind
  counterparts: Array<{
    counterpart_kind: CounterpartKind
    dt_account_id: number
    ct_account_id: number
    amount: string
    dt_apx_id?: number
    ct_apx_id?: number
    note?: string
    doc_nom?: string
    doc_date?: string
    mirage_open_item_ref?: unknown
    dt_apx_part?: string
    ct_apx_part?: string
  }>
}

export function useCreateMatch() {
  const qc = useQueryClient()
  return useMutation(
    (payload: CreateMatchPayload) =>
      reconFetch<Match>('/api/v1/reconciliation/matches', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    {
      onSuccess: () => {
        qc.invalidateQueries(['recon', 'matches'])
        qc.invalidateQueries(['recon', 'transactions'])
      },
    }
  )
}

interface UpdateMatchPayload {
  id: string
  client_id: string
  match_kind: MatchKind
  counterparts: CreateMatchPayload['counterparts']
}

export function useUpdateMatch() {
  const qc = useQueryClient()
  return useMutation(
    (payload: UpdateMatchPayload) =>
      reconFetch<Match>(`/api/v1/reconciliation/matches/${payload.id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    {
      onSuccess: () => {
        qc.invalidateQueries(['recon', 'matches'])
        qc.invalidateQueries(['recon', 'transactions'])
      },
    }
  )
}

export interface FXRateResult {
  available: boolean
  rate?: string
  source?: string
  base_code: string
}

export function useFXRate(
  clientId: string | null,
  currency: string | null,
  date: string | null,
) {
  return useQuery<FXRateResult>(
    ['recon', 'fx-rate', clientId, currency, date],
    () =>
      reconFetch<FXRateResult>(
        `/api/v1/reconciliation/fx-rate?client_id=${clientId}&currency=${currency}&date=${date}`,
      ),
    {
      enabled: !!clientId && !!currency && !!date,
      staleTime: 60_000,
    },
  )
}

// Trigger BNB/ECB fetch via the Python backend, then re-query the rate.
export function useRefreshFXRate() {
  const qc = useQueryClient()
  return useMutation(
    async (payload: { date: string; currency?: string }) => {
      const params = new URLSearchParams({ target_date: payload.date })
      if (payload.currency) params.set('currency_code', payload.currency)
      await apiClient.post(`/currency-rates/rates/fetch?${params.toString()}`, {})
    },
    {
      onSuccess: () => qc.invalidateQueries(['recon', 'fx-rate']),
    },
  )
}

export function useCommitMatches() {
  const qc = useQueryClient()
  return useMutation(
    (payload: {
      match_ids: string[]
      client_id: string
      doc_nom?: string
      reg_date?: string
      fx_rate?: string
      include_fx_adjustment?: boolean
    }) =>
      reconFetch<CommitResult>('/api/v1/reconciliation/matches/commit', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    { onSuccess: () => qc.invalidateQueries(['recon', 'matches']) }
  )
}

export function useReverseMatch() {
  const qc = useQueryClient()
  return useMutation(
    (payload: { id: string; client_id: string; hard_delete?: boolean }) =>
      reconFetch<Match>(
        `/api/v1/reconciliation/matches/${payload.id}/reverse`,
        {
          method: 'POST',
          body: JSON.stringify({ client_id: payload.client_id, hard_delete: payload.hard_delete ?? false }),
        }
      ),
    { onSuccess: () => qc.invalidateQueries(['recon', 'matches']) }
  )
}

export function usePostings(
  clientId: string | null,
  accountId: number | null,
  filters?: { dateFrom?: string; dateTo?: string; amountMin?: number; amountMax?: number; search?: string }
) {
  const params = new URLSearchParams()
  if (filters?.dateFrom) params.set('date_from', filters.dateFrom)
  if (filters?.dateTo) params.set('date_to', filters.dateTo)
  if (filters?.amountMin != null) params.set('amount_min', String(filters.amountMin))
  if (filters?.amountMax != null) params.set('amount_max', String(filters.amountMax))
  if (filters?.search) params.set('search', filters.search)
  const qs = params.toString()

  return useQuery<MiragePosting[]>(
    ['recon', 'postings', clientId, accountId, qs],
    () =>
      reconFetch<MiragePosting[]>(
        `/api/v1/reconciliation/clients/${clientId}/accounts/${accountId}/postings${qs ? `?${qs}` : ''}`
      ),
    { enabled: !!clientId && accountId != null }
  )
}

export function useLinkExistingEntry() {
  const qc = useQueryClient()
  return useMutation(
    (payload: {
      transaction_id: string
      client_id: string
      mirage_bank_account_id: number
      acc_rec_d_id: number
    }) =>
      reconFetch<Match>('/api/v1/reconciliation/matches/link', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    {
      onSuccess: () => {
        qc.invalidateQueries(['recon', 'matches'])
        qc.invalidateQueries(['recon', 'transactions'])
      },
    }
  )
}

export function useRunAutoMatch() {
  const qc = useQueryClient()
  return useMutation(
    (payload: { client_id: string; statement_id: string; bank_account_id: number }) =>
      reconFetch<{ job_id: string }>(
        `/api/v1/reconciliation/clients/${payload.client_id}/statements/${payload.statement_id}/auto-match`,
        {
          method: 'POST',
          body: JSON.stringify({ bank_account_id: payload.bank_account_id }),
        }
      ),
    { onSuccess: () => qc.invalidateQueries(['recon', 'matches']) }
  )
}

interface AutoMatchJob {
  job_id: string
  status: 'running' | 'completed' | 'failed'
  total_transactions: number
  processed_count: number
  matched_count: number
  error?: string
}

export function useAutoMatchJob(jobId: string | null) {
  const qc = useQueryClient()
  return useQuery<AutoMatchJob>(
    ['recon', 'auto-match-job', jobId],
    () => reconFetch<AutoMatchJob>(`/api/v1/reconciliation/auto-match/jobs/${jobId}`),
    {
      enabled: !!jobId,
      refetchInterval: (data) => {
        if (!data) return 2000
        return data.status === 'running' ? 2000 : false
      },
      onSuccess: () => {
        qc.invalidateQueries(['recon', 'matches'])
      },
    }
  )
}
